/**
 * GitHub OAuth (PKCE) for publishing to koiro-dev-blog without Supabase.
 * Set REACT_APP_GITHUB_OAUTH_CLIENT_ID in .env (from GitHub OAuth App).
 */

const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_OAUTH_CLIENT_ID;
const REDIRECT_URI = typeof window !== 'undefined'
  ? `${window.location.origin}/dev-blog/github-callback`
  : '';
const SCOPE = 'public_repo';
const DRAFT_KEY = 'jekyll-publish-draft';
const VERIFIER_KEY = 'github_oauth_code_verifier';
const PAT_KEY = 'github_publish_pat';
const ALLOWED_GITHUB_LOGIN = 'calicorone';

function base64Encode(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
}

function base64UrlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

export async function getCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

export function getAuthUrl() {
  if (!GITHUB_CLIENT_ID || !REDIRECT_URI) return null;
  return (async () => {
    const verifier = await generateCodeVerifier();
    const challenge = await getCodeChallenge(verifier);
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    const params = new URLSearchParams({
      client_id: GITHUB_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: SCOPE,
      response_type: 'code',
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    return `https://github.com/login/oauth/authorize?${params.toString()}`;
  })();
}

/**
 * @returns {{ token: string | null, error?: 'no_verifier' | 'no_client_id' | 'github_error' }}
 */
export async function exchangeCodeForToken(code) {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!GITHUB_CLIENT_ID || !REDIRECT_URI) {
    return { token: null, error: 'no_client_id' };
  }
  if (!code || !verifier) {
    return { token: null, error: 'no_verifier' };
  }

  try {
    const apiUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/exchange-github-token`;
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        code_verifier: verifier,
        redirect_uri: REDIRECT_URI,
      }),
    });
    const text = await res.text();
    if (res.status === 405) {
      return { token: null, error: 'api_405' };
    }
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        return { token: null, error: 'github_error' };
      }
    }
    if (res.ok && data.access_token) return { token: data.access_token };
    return { token: null, error: 'github_error' };
  } catch (e) {
    throw e;
  }
}

export function saveDraft(draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (e) {
    // ignore
  }
}

export function loadDraft() {
  try {
    const s = sessionStorage.getItem(DRAFT_KEY);
    if (s) {
      sessionStorage.removeItem(DRAFT_KEY);
      return JSON.parse(s);
    }
  } catch (e) {
    // ignore
  }
  return null;
}

export function isGitHubAuthAvailable() {
  return !!GITHUB_CLIENT_ID && !!REDIRECT_URI;
}

// --- PAT (Personal Access Token): 서버 없이 브라우저에서 바로 게시 ---

const ENV_PAT = typeof process !== 'undefined' && process.env.REACT_APP_GITHUB_PUBLISH_TOKEN
  ? String(process.env.REACT_APP_GITHUB_PUBLISH_TOKEN).trim()
  : '';

export function getStoredToken() {
  try {
    const fromSession = sessionStorage.getItem(PAT_KEY) || '';
    if (fromSession) return fromSession;
    return ENV_PAT;
  } catch {
    return ENV_PAT;
  }
}

/** .env에 토큰이 설정되어 있으면 true */
export function hasEnvToken() {
  return !!ENV_PAT;
}

export function setStoredToken(token, save) {
  try {
    if (save && token) sessionStorage.setItem(PAT_KEY, token);
    else if (!save) sessionStorage.removeItem(PAT_KEY);
  } catch {}
}

/**
 * PAT로 글을 GitHub에 올림. 서버/콜백 불필요.
 * @param {{ title: string, date: string, content: string, slug: string }} draft
 * @param {string} token GitHub PAT (repo 권한)
 * @returns {{ ok: boolean, url?: string, error?: string }}
 */
export async function publishWithToken(draft, token) {
  if (!draft?.title || !draft?.content || !draft?.slug || !draft?.date) {
    return { error: '제목·본문·날짜가 필요합니다.' };
  }
  const t = (token || getStoredToken()).trim();
  if (!t) return { error: 'GitHub 토큰을 입력해 주세요.' };

  try {
    const userRes = await fetch('https://api.github.com/user', {
      headers: { Authorization: `Bearer ${t}` },
    });
    const userText = await userRes.text();
    let user = {};
    if (userText) {
      try {
        user = JSON.parse(userText);
      } catch {
        return { error: 'GitHub 사용자 정보를 읽지 못했습니다.' };
      }
    }
    if (user?.login !== ALLOWED_GITHUB_LOGIN) {
      return { error: '이 계정은 게시할 수 없습니다. (calicorone만 가능)' };
    }

    const path = `content/posts/${draft.date}-${draft.slug}.md`;
    const contentBase64 = base64Encode(draft.content);
    const putRes = await fetch(
      `https://api.github.com/repos/calicorone/koiro-dev-blog/contents/${path}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${t}`,
          Accept: 'application/vnd.github.v3+json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: `Add post: ${draft.title}`,
          content: contentBase64,
          branch: 'master',
        }),
      }
    );
    if (!putRes.ok) {
      const errText = await putRes.text();
      return { error: 'GitHub에 올리지 못했습니다.', detail: errText.slice(0, 200) };
    }
    return {
      ok: true,
      url: `https://blog.koiro.me/${draft.date.replace(/-/g, '/')}/${draft.slug}/`,
    };
  } catch (e) {
    return { error: e?.message || '요청에 실패했습니다.' };
  }
}
